import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  { label: 'Home',    to: '/' },
  { label: 'About',   to: '/about' },
  { label: 'R&D',     to: '/rd' },
  { label: 'Team',    to: '/team' },
  { label: 'News',    to: '/news' },
  { label: 'Careers', to: '/careers' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open,     setOpen]     = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => { setOpen(false); }, [location]);

  const isActive = (to) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  const linkClass = (to) =>
    `relative text-sm font-medium tracking-wide transition-colors duration-300 py-1
     after:absolute after:bottom-[-3px] after:left-0 after:h-[1.5px] after:rounded-full
     after:bg-gradient-to-r after:from-teal after:to-purple after:transition-[width] after:duration-300
     ${isActive(to)
       ? 'text-white after:w-full'
       : 'text-white/60 hover:text-white after:w-0 hover:after:w-full'}`;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 h-[72px] z-[1000] flex items-center transition-all duration-500
          ${scrolled ? 'bg-[rgba(4,6,15,0.88)] backdrop-blur-2xl border-b shadow-[0_4px_40px_rgba(0,0,0,0.6)]' : ''}`}
        style={scrolled ? { borderBottomColor: 'rgba(0,255,255,0.05)' } : {}}
      >
        {scrolled && (
          <div className="absolute bottom-0 left-0 right-0 h-px pointer-events-none"
            style={{ background: 'linear-gradient(90deg,transparent,rgba(0,255,255,0.4) 30%,rgba(139,92,246,0.4) 70%,transparent)', animation: 'navScan 4s linear infinite' }} />
        )}

        <div className="w-full max-w-[1240px] mx-auto px-7 flex items-center gap-10">

          {/* Logo */}
          <Link to="/" className="flex items-baseline gap-1 flex-shrink-0 no-underline">
            <span className="font-display font-extrabold text-[1.6rem] text-white tracking-[-0.04em]">D2S</span>
            <span className="font-mono text-[0.58rem] font-medium tracking-[6px] uppercase"
              style={{ color: '#00ffff', textShadow: '0 0 14px rgba(0,255,255,0.6)', animation: 'labGlow 3s ease-in-out infinite' }}>
              LAB
            </span>
          </Link>

          {/* Desktop links — same tab via React Router Link */}
          <ul className="hidden md:flex items-center gap-8 flex-1">
            {NAV_LINKS.map(({ label, to }) => (
              <li key={label}>
                <Link to={to} className={linkClass(to)} style={{ fontFamily: 'var(--font-body)' }}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>

          {/* CTA */}
          <Link
            to="/contact"
            className="hidden md:inline-flex btn btn--primary ml-auto !py-[9px] !px-[22px] !text-[0.82rem] !font-bold flex-shrink-0"
          >
            Contact Us
          </Link>

          {/* Hamburger */}
          <button
            onClick={() => setOpen(o => !o)}
            className="md:hidden ml-auto flex flex-col gap-[5px] bg-transparent border-none cursor-pointer p-2 z-[1002]"
            aria-label="Toggle menu"
          >
            {[1, 2, 3].map(i => (
              <span key={i} className={`block w-6 h-[2px] bg-white rounded-sm transition-all duration-300
                ${open && i === 1 ? 'rotate-45 translate-x-[5px] translate-y-[5px]' : ''}
                ${open && i === 2 ? 'opacity-0 w-0' : ''}
                ${open && i === 3 ? '-rotate-45 translate-x-[5px] -translate-y-[5px]' : ''}`}
              />
            ))}
          </button>
        </div>
      </nav>

      {/* Mobile drawer — same tab via React Router Link */}
      <div
        className={`fixed inset-0 z-[999] flex items-center justify-center transition-opacity duration-400
          ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        style={{ background: 'rgba(4,6,15,0.98)', backdropFilter: 'blur(40px)' }}
      >
        <ul className="flex flex-col gap-9 text-center">
          {NAV_LINKS.map(({ label, to }) => (
            <li key={label}>
              <Link
                to={to}
                onClick={() => setOpen(false)}
                className={`font-display text-[2.2rem] font-bold tracking-[-0.04em] transition-colors duration-300
                  ${isActive(to) ? 'text-white' : 'text-white/50 hover:text-white'}`}
              >
                {label}
              </Link>
            </li>
          ))}
          <li>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="font-display text-[1.5rem] font-bold"
              style={{ color: '#00ffff', textShadow: '0 0 20px rgba(0,255,255,0.4)' }}
            >
              Contact Us
            </Link>
          </li>
        </ul>
      </div>
    </>
  );
}
